import { useTripBuilder } from '../TripBuilderContext';
import DestImage from './DestImage';

export default function MockupCompare({ onLoad, onClose }) {
  const tb = useTripBuilder();

  if (tb.mockups.length === 0) return null;

  return (
    <div className="bg-navy-light rounded-2xl border border-brand-ink/10 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg text-white">Saved Trip Plans</h3>
          <div className="text-xs text-brand-ink/55 mt-0.5">Compare your saved trip plans side by side.</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-xs text-brand-ink/45 hover:text-brand-ink/65">hide</button>
        )}
      </div>

      <div className={`grid gap-4 ${tb.mockups.length === 1 ? 'grid-cols-1 max-w-md' : tb.mockups.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {tb.mockups.map(m => (
          <div key={m.id} className="bg-brand-ink/5 rounded-xl border border-brand-ink/10 overflow-hidden hover:border-brand-ink/20 transition group">
            {/* Hero image */}
            {m.destination && (
              <div className="h-28 relative overflow-hidden">
                <DestImage city={m.destination.city} imgClassName="group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-transparent" />
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="font-bold text-white text-sm">{m.name}</div>
                  <div className="text-[10px] text-brand-ink/75">{m.destination.city}, {m.destination.country}</div>
                </div>
              </div>
            )}

            <div className="p-3 space-y-2.5">
              {/* Dates */}
              {m.startDate && m.endDate && (
                <div className="text-[10px] text-brand-ink/65">
                  {new Date(m.startDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' - '}
                  {new Date(m.endDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {m.costs?.nights && ` (${m.costs.nights} nights)`}
                </div>
              )}

              {/* Cost breakdown */}
              <div className="space-y-1">
                <CostRow label="Flights" value={m.costs?.flightPoints} suffix="pts" color="text-blue-300" />
                <CostRow label="Taxes" value={m.costs?.flightTaxes} prefix="$" color="text-brand-ink/70" />
                <CostRow label="Hotel" value={m.costs?.hotelPoints} suffix="pts" color="text-indigo-300" />
                <CostRow label="Activities" value={m.costs?.activityCash} prefix="$" color="text-gold" />
                <CostRow label="Food" value={m.costs?.restaurantCash} prefix="$" color="text-gold" />
              </div>

              {/* Totals */}
              <div className="border-t border-brand-ink/10 pt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Total Points</span>
                  <span className="font-bold text-teal">{m.costs?.totalPoints?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Total Cash</span>
                  <span className="font-bold text-gold">${m.costs?.totalCash?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Points Left</span>
                  <span className={`font-medium ${(m.costs?.amexRemaining + m.costs?.chaseRemaining) < 0 ? 'text-danger' : 'text-brand-ink/75'}`}>
                    {((m.costs?.amexRemaining || 0) + (m.costs?.chaseRemaining || 0)).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Items count */}
              <div className="flex gap-2 text-[10px] text-brand-ink/55">
                {m.flight && <span>{m.flight.airline_program}</span>}
                {m.hotel && <span>&middot; {m.hotel.hotel_name}</span>}
                {m.activities?.length > 0 && <span>&middot; {m.activities.length} activities</span>}
                {m.restaurants?.length > 0 && <span>&middot; {m.restaurants.length} restaurants</span>}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => onLoad && onLoad(m)}
                  className="flex-1 py-2 bg-teal/20 text-teal text-xs font-bold rounded-lg hover:bg-teal/30 transition"
                >
                  Load this plan
                </button>
                <button
                  onClick={() => tb.deleteMockup(m.id)}
                  className="px-3 py-2 text-xs text-brand-ink/45 hover:text-danger transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CostRow({ label, value, prefix = '', suffix = '', color }) {
  if (!value || value === 0) return null;
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-brand-ink/55">{label}</span>
      <span className={color}>{prefix}{value.toLocaleString()} {suffix}</span>
    </div>
  );
}
